import React, { useState } from 'react';
import { CircleInfoIcon, TriangleExclamationIcon, XmarkIcon, CircleCheckIcon } from '@revfluence/fresh-icons/solid/esm';
import { Alert as ShadcnAlert, AlertDescription } from '@frontend/shadcn/components/ui/alert';
import { Button } from '@frontend/shadcn/components/ui/button';
import { cn } from '@frontend/shadcn/lib/utils';

export type AlertType = 'info' | 'warning' | 'error' | 'success';
export type AlertVariant = 'default' | 'filled';

// Alert Button Component
interface AlertButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
}

export const AlertButton = React.forwardRef<HTMLButtonElement, AlertButtonProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant="outline"
        size="sm"
        className={cn(
          "font-['Inter'] font-medium text-[14px] leading-[24px] text-[#16282d] bg-[#ffffff] border-[#e5e8eb] hover:bg-[#f8f9fb] hover:border-[#cbd1d7] px-4 py-1 h-auto whitespace-nowrap rounded-lg shadow-sm",
          className,
        )}
        {...props}
      >
        {children}
      </Button>
    );
  },
);
AlertButton.displayName = 'AlertButton';

// Alert Dismiss Button Component
interface AlertDismissButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: AlertVariant;
  className?: string;
}

export const AlertDismissButton = React.forwardRef<HTMLButtonElement, AlertDismissButtonProps>(
  ({ variant = 'default', className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        className={cn(
          'h-6 w-6 p-0 hover:bg-[#cbd1d7] hover:opacity-100 flex-shrink-0 rounded-md self-start',
          className,
        )}
        aria-label="Dismiss alert"
        {...props}
      >
        <XmarkIcon className={`h-4 w-4 ${variant === 'filled' ? 'text-white' : 'text-[#5d6974]'}`} />
      </Button>
    );
  },
);
AlertDismissButton.displayName = 'AlertDismissButton';

// Alert Icon Component
interface AlertIconProps {
  type: AlertType;
  variant?: AlertVariant;
  className?: string;
}

export const AlertIcon: React.FC<AlertIconProps> = ({ type, variant = 'default', className }) => {
  const iconColor = variant === 'filled' ? 'text-white' : type === 'error' ? 'text-[#cb4349]' : 'text-[#16282d]';

  const iconMap = {
    info: <CircleInfoIcon className={cn(`w-5 h-5 ${iconColor} flex-shrink-0`, className)} />,
    warning: <TriangleExclamationIcon className={cn(`w-5 h-5 ${iconColor} flex-shrink-0`, className)} />,
    error: <TriangleExclamationIcon className={cn(`w-5 h-5 ${iconColor} flex-shrink-0`, className)} />,
    success: <CircleCheckIcon className={cn(`w-5 h-5 ${iconColor} flex-shrink-0`, className)} />,
  };

  return iconMap[type];
};

// Alert Title Component
interface AlertTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: AlertVariant;
  className?: string;
}

export const AlertTitle = React.forwardRef<HTMLDivElement, AlertTitleProps>(
  ({ children, variant = 'default', className, ...props }, ref) => {
    const textColor = variant === 'filled' ? 'text-white' : 'text-[#16282d]';

    return (
      <div
        ref={ref}
        className={cn(`font-['Inter'] font-medium text-[16px] leading-[28px] ${textColor}`, className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);
AlertTitle.displayName = 'AlertTitle';

// Alert Description Component
interface AlertDescriptionTextProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: AlertVariant;
  className?: string;
}

export const AlertDescriptionText = React.forwardRef<HTMLDivElement, AlertDescriptionTextProps>(
  ({ children, variant = 'default', className, ...props }, ref) => {
    const descriptionColor = variant === 'filled' ? 'text-white opacity-90' : 'text-[#16282d]';

    return (
      <AlertDescription
        ref={ref}
        className={cn(`font-['Inter'] font-normal text-[14px] leading-[24px] ${descriptionColor} m-0`, className)}
        {...props}
      >
        {children}
      </AlertDescription>
    );
  },
);
AlertDescriptionText.displayName = 'AlertDescriptionText';

// Alert Header Component (Icon + Title)
interface AlertHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const AlertHeader = React.forwardRef<HTMLDivElement, AlertHeaderProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex items-center gap-2', className)} {...props}>
        {children}
      </div>
    );
  },
);
AlertHeader.displayName = 'AlertHeader';

// Alert Content Component (Header + Description)
interface AlertContentProps extends React.HTMLAttributes<HTMLDivElement> {}

export const AlertContent = React.forwardRef<HTMLDivElement, AlertContentProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex flex-col gap-2 flex-1 min-w-0', className)} {...props}>
        {children}
      </div>
    );
  },
);
AlertContent.displayName = 'AlertContent';

// Alert Actions Component (Button container)
interface AlertActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
}

export const AlertActions = React.forwardRef<HTMLDivElement, AlertActionsProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex-shrink-0', className)} {...props}>
        {children}
      </div>
    );
  },
);
AlertActions.displayName = 'AlertActions';

// Alert Main Content Component (wrapper for main content area)
interface AlertMainContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
}

export const AlertMainContent = React.forwardRef<HTMLDivElement, AlertMainContentProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex items-center gap-4 pr-10', className)} {...props}>
        {children}
      </div>
    );
  },
);
AlertMainContent.displayName = 'AlertMainContent';

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  type: AlertType;
  variant: AlertVariant;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ type, variant, children, className, ...props }, ref) => {
    return (
      <ShadcnAlert
        ref={ref}
        className={cn('flex items-center gap-4', alertStyles[type][variant], className)}
        {...props}
      >
        {children}
      </ShadcnAlert>
    );
  },
);
Alert.displayName = 'Alert';

export interface SocialAlertProps {
  type?: AlertType;
  variant?: AlertVariant;
  title: string;
  description: string;
  button?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
  icon?: React.ReactNode;
}

// Alert styles for different types and variants
const alertStyles: Record<AlertType, Record<AlertVariant, string>> = {
  info: {
    default: 'bg-[#e5e8eb] border border-[#cbd1d7] text-[#16282d] rounded-2xl px-4 py-4 relative',
    filled: 'bg-[#3b82f6] border border-[#2563eb] text-white rounded-2xl px-4 py-4 relative',
  },
  warning: {
    default: 'bg-[#e5e8eb] border border-[#cbd1d7] text-[#16282d] rounded-2xl px-4 py-4 relative',
    filled: 'bg-[#f59e0b] border border-[#d97706] text-white rounded-2xl px-4 py-4 relative',
  },
  error: {
    default: 'bg-[#f4cdbc] border border-[#f4a88a] text-[#16282d] rounded-2xl px-4 py-4 relative',
    filled: 'bg-[#ef4444] border border-[#dc2626] text-white rounded-2xl px-4 py-4 relative',
  },
  success: {
    default: 'bg-[#d1fae5] border border-[#a7f3d0] text-[#16282d] rounded-2xl px-4 py-4 relative',
    filled: 'bg-[#10b981] border border-[#059669] text-white rounded-2xl px-4 py-4 relative',
  },
};

// Single Alert Component (following Shadcn patterns)
export const SocialAlert: React.FC<SocialAlertProps> = ({
  type = 'info',
  variant = 'default',
  title,
  description,
  button,
  dismissible = true,
  icon,
  onDismiss,
  ...props
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  if (isDismissed) return null;

  return (
    <Alert type={type} variant={variant} {...props}>
      <AlertContent>
        <AlertHeader>
          {icon || <AlertIcon type={type} variant={variant} />}
          <AlertTitle variant={variant}>{title}</AlertTitle>
        </AlertHeader>
        <AlertDescriptionText variant={variant}>{description}</AlertDescriptionText>
      </AlertContent>
      {button && <AlertActions>{button}</AlertActions>}
      {dismissible && <AlertDismissButton variant={variant} onClick={handleDismiss} />}
    </Alert>
  );
};

export default SocialAlert;
